import { APP_LOGO } from "@/const";
import SiteFooter from "@/components/SiteFooter";
import TypewriterText from "@/components/TypewriterText";
import { CardCarousel } from "@/components/CardCarousel";
import { featuredArticles } from "@/data/featuredArticles";
import {
  Mail, MessageCircle, X, FileText, Play, ChevronDown,
  Award, Camera, MapPin, ArrowRight, Star, Shield, Zap, Eye
} from "lucide-react";
import Navbar from "@/components/Navbar";
import { useState, useEffect, useRef, useCallback, lazy, Suspense } from "react";
import { useAnalytics } from "@/hooks/useAnalytics";
import { Link, useLocation } from "wouter";

// Differes : App.tsx decoupe deja CategoryPage en paquet separe, mais
// l'importer ici le ramenait dans le paquet d'entree. Les trois autres ne sont
// rendus qu'en bas de page. Chargement a la demande, repli neutre.
const CategoryPage = lazy(() => import("./CategoryPage"));
const VideoFrame = lazy(() => import("@/components/VideoFrame"));
const PriceCalculator = lazy(() => import("@/components/PriceCalculator"));
const QuizConversion = lazy(() => import("@/components/QuizConversion"));
const TestimonialCarousel = lazy(() => import("@/components/TestimonialCarousel"));

// ─── Hook parallaxe hero ──────────────────────────────────────────────────────
function useParallax() {
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const handleScroll = () => setOffset(window.scrollY * 0.35);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return offset;
}

// ─── Hook spotlight (lumière qui suit la souris sur les cartes) ───────────────
function useSpotlight(ref: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handleMouseMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      el.style.setProperty("--mouse-x", `${x}%`);
      el.style.setProperty("--mouse-y", `${y}%`);
    };
    el.addEventListener("mousemove", handleMouseMove);
    return () => el.removeEventListener("mousemove", handleMouseMove);
  }, [ref]);
}

// ─── Hook scroll reveal ───────────────────────────────────────────────────────
function useScrollReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    const selectors = [
      ".reveal", ".reveal-left", ".reveal-right", ".reveal-scale", ".stagger-parent",
      ".scroll-slide-left", ".scroll-slide-right", ".scroll-scale-up",
      ".scroll-blur-in", ".scroll-rotate-in", ".scroll-clip-bottom",
      ".scroll-flip-in", ".scroll-badge-pop", ".section-divider", ".scroll-line-expand"
    ].join(", ");
    document.querySelectorAll(selectors).forEach((el) =>
      observer.observe(el)
    );
    return () => observer.disconnect();
  }, []);
}

// ─── Hook hero reveal ─────────────────────────────────────────────────────────
function useHeroReveal(delay = 200) {
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setRevealed(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  return revealed;
}

// ─── Hook compteur animé ──────────────────────────────────────────────────────
function useCounter(target: number, duration = 2000, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

// ─── Hook 3D tilt sur les cartes ─────────────────────────────────────────────
function useTilt(ref: React.RefObject<HTMLElement | null>, intensity = 8) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handleMouseMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = (e.clientX - cx) / (rect.width / 2);
      const dy = (e.clientY - cy) / (rect.height / 2);
      el.style.transform = `perspective(800px) rotateY(${dx * intensity}deg) rotateX(${-dy * intensity}deg) scale(1.02)`;
    };
    const handleMouseLeave = () => {
      el.style.transform = "perspective(800px) rotateY(0deg) rotateX(0deg) scale(1)";
      el.style.transition = "transform 0.6s cubic-bezier(0.16, 1, 0.3, 1)";
    };
    const handleMouseEnter = () => {
      el.style.transition = "transform 0.1s ease";
    };
    el.addEventListener("mousemove", handleMouseMove);
    el.addEventListener("mouseleave", handleMouseLeave);
    el.addEventListener("mouseenter", handleMouseEnter);
    return () => {
      el.removeEventListener("mousemove", handleMouseMove);
      el.removeEventListener("mouseleave", handleMouseLeave);
      el.removeEventListener("mouseenter", handleMouseEnter);
    };
  }, [ref, intensity]);
}

// ─── Composant vidéo Valentine : démarrage auto à 15 s, sans boucle ─────────────
function VideoWithSound() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [soundEnabled, setSoundEnabled] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Démarrage automatique au bout de 15 secondes (sans boucle)
    const timer = setTimeout(() => {
      if (!video) return;
      video.muted = true; // garantit le démarrage
      video
        .play()
        .then(() => {
          video.muted = false;
          video.volume = 0.85;
          setSoundEnabled(true);
        })
        .catch(() => {});
    }, 15000);

    return () => clearTimeout(timer);
  }, []);

  const toggleSound = () => {
    const video = videoRef.current;
    if (!video) return;
    if (soundEnabled) {
      video.muted = true;
      setSoundEnabled(false);
    } else {
      video.muted = false;
      video.volume = 0.85;
      setSoundEnabled(true);
    }
  };

  return (
    <div className="relative">
      <video
        ref={videoRef}
        controls
        playsInline
        muted
        preload="none"
        className="w-full h-auto block"
        poster="/og-veastudio.png"
      >
        <source
          src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663031358771/ouOMWKoPgmwZAKBA.mp4"
          type="video/mp4"
        />
        Votre navigateur ne supporte pas la lecture de vidéos.
      </video>
      {/* Badge son */}
      <button
        onClick={toggleSound}
        className="absolute bottom-3 right-3 flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-widest transition-all duration-300"
        style={{
          background: soundEnabled ? "rgba(201,168,76,0.15)" : "rgba(13,13,13,0.85)",
          border: `1px solid ${soundEnabled ? "var(--vea-or)" : "rgba(255,255,255,0.2)"}`,
          color: soundEnabled ? "var(--vea-or)" : "rgba(255,255,255,0.5)",
          fontFamily: "'Montserrat', sans-serif",
          backdropFilter: "blur(8px)",
        }}
      >
        {soundEnabled ? (
          <>
            <span style={{ fontSize: "0.9em" }}>🔊</span> Son activé
          </>
        ) : (
          <>
            <span style={{ fontSize: "0.9em" }}>🔇</span> Son coupé
          </>
        )}
      </button>
    </div>
  );
}

// ─── Composant stat animée ────────────────────────────────────────────────────
function AnimatedStat({ value, suffix, label, icon: Icon, delay = 0 }: {
  value: number; suffix: string; label: string; icon?: React.ElementType; delay?: number;
}) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const count = useCounter(value, 2200, visible);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="stagger-child text-center group"
      style={{ transitionDelay: `${delay}ms` }}
    >
      {Icon && (
        <div className="flex justify-center mb-3">
          <div
            className="w-10 h-10 flex items-center justify-center"
            style={{
              border: "1px solid rgba(201,168,76,0.3)",
              background: "rgba(201,168,76,0.05)",
              transition: "all 0.3s ease",
            }}
          >
            <Icon className="w-4 h-4" style={{ color: "var(--vea-or)" }} />
          </div>
        </div>
      )}
      <div
        className="text-5xl md:text-6xl font-bold mb-2 tabular-nums"
        style={{ fontFamily: "'Playfair Display', serif", color: "var(--vea-or)" }}
      >
        {count}{suffix}
      </div>
      <div
        className="text-xs uppercase tracking-widest"
        style={{ color: "var(--vea-gris-texte)", fontFamily: "'Montserrat', sans-serif" }}
      >
        {label}
      </div>
    </div>
  );
}

// ─── Composant carte service avec tilt 3D ────────────────────────────────────
function ServiceCard({ service, index }: { service: any; index: number }) {
  const cardRef = useRef<HTMLDivElement>(null);
  useTilt(cardRef, 6);

  return (
    <Link href={service.url} className="block">
      <div
        ref={cardRef}
        className="service-card-premium reveal"
        style={{
          transitionDelay: `${index * 80}ms`,
          willChange: "transform",
          transformStyle: "preserve-3d",
        }}
      >
        {/* Image avec Ken Burns */}
        <div className="service-card-img">
          <CardCarousel
            images={service.images}
            title={service.title}
            interval={4000 + index * 300}
            aspectRatio="3/4"
          />
        </div>

        {/* Badge catégorie */}
        <div className="service-card-badge">
          <span style={{ fontFamily: "'Montserrat', sans-serif" }}>{service.badge}</span>
        </div>

        {/* Overlay qui monte depuis le bas */}
        <div className="service-card-overlay">
          <div className="service-card-line" />
          <h3 className="service-card-title">{service.title}</h3>
          <p className="service-card-desc">{service.description}</p>
          <div className="service-card-cta">
            <span style={{ fontFamily: "'Montserrat', sans-serif" }}>Découvrir</span>
            <span className="service-card-arrow">→</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────
export default function Home() {
  const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  const category = params.get("category");

  if (
    category === "mariage" || category === "immobilier" ||
    category === "entreprise" || category === "association" ||
    category === "institutionnel"
  ) {
    return (
      <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
        <CategoryPage />
      </Suspense>
    );
  }

  const [showBanner, setShowBanner] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("vea-banner-dismissed") !== "true";
    }
    return true;
  });

  const whatsappNumber = "33643166706";

  const services = [
    {
      title: "Mariages",
      description: "Pour immortaliser le plus beau jour de votre vie sous un angle unique",
      images: [
        "https://images.unsplash.com/photo-1519741497674-611481863552?w=800&q=80",
        "https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=800&q=80",
        "https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?w=800&q=80",
        "https://images.unsplash.com/photo-1606800052052-a08af7148866?w=800&q=80",
        "https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=800&q=80",
      ],
      url: "/mariage",
      badge: "Mariages",
    },
    {
      title: "Agences Immobilières",
      description: "Pour sublimer vos biens et booster vos ventes",
      images: [
        "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=80",
        "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=800&q=80",
        "https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=800&q=80",
        "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&q=80",
        "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=800&q=80",
      ],
      url: "/immobilier",
      badge: "Immobilier",
    },
    {
      title: "Entreprises",
      description: "Pour vos vidéos corporate, expertises ou événements",
      images: [
        "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80",
        "https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80",
        "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80",
        "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=80",
        "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800&q=80",
      ],
      url: "/entreprises",
      badge: "Corporate",
    },
    {
      title: "Associations",
      description: "Pour valoriser vos actions et fédérer votre communauté",
      images: [
        "https://images.unsplash.com/photo-1511578314322-379afb476865?w=800&q=80",
        "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&q=80",
        "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=800&q=80",
        "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=800&q=80",
      ],
      url: "/associations",
      badge: "Associations",
    },
    {
      title: "Institutionnel",
      description: "Pour les départements, régions et collectivités territoriales",
      images: [
        "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=800&q=80",
        "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=800&q=80",
        "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=800&q=80",
      ],
      url: "/institutionnel-categorie",
      badge: "Institutionnel",
    },
    {
      title: "Experts Judiciaires",
      description: "Constats visuels précis pour assurances, couvreurs et expertises sinistres",
      images: [
        "/expertise-incendie.webp",
        "/expertise-inondation.webp",
        "/expertise-chantier.webp",
      ],
      url: "/experts-judiciaires",
      badge: "Expertise",
    },
  ];

  const { trackWhatsAppClick, trackEmailClick } = useAnalytics();
  useScrollReveal();
  const parallaxOffset = useParallax();
  const heroRevealed = useHeroReveal(300);
  const statsRef = useRef<HTMLElement>(null);
  const manifesteRef = useRef<HTMLElement>(null);
  useSpotlight(statsRef);

  const handleWhatsApp = (service: string) => {
    trackWhatsAppClick("home", service);
    const message = encodeURIComponent(
      `Bonjour Valentine, je suis intéressé(e) par vos prestations drone pour ${service}. Pouvez-vous me donner plus d'informations ?`
    );
    window.open(`https://wa.me/${whatsappNumber}?text=${message}`, "_blank");
  };

  const handleEmail = (service: string) => {
    trackEmailClick("home");
    const subject = encodeURIComponent(`Demande d'information - Prestation Drone ${service}`);
    const body = encodeURIComponent(
      `Bonjour Valentine,\n\nJe suis intéressé(e) par vos prestations drone pour ${service}.\n\nPouvez-vous me donner plus d'informations ?\n\nCordialement`
    );
    window.location.href = `mailto:contact@veastudio.fr?subject=${subject}&body=${body}`;
  };

  return (
    <div className="min-h-screen grain-overlay" style={{ background: "var(--vea-noir-cinema)", color: "var(--vea-blanc)" }}>
      {/* ─── NAVBAR ──────────────────────────────────────────────────────────── */}
      <Navbar
        topBanner={
          showBanner ? (
            <div
              className="relative py-2.5 px-4"
              style={{
                background:
                  "radial-gradient(120% 180% at 50% 0%, rgba(166,30,45,0.85) 0%, rgba(139,24,36,0.55) 35%, rgba(13,13,13,0.95) 100%)",
                borderBottom: "1px solid rgba(201,168,76,0.18)",
              }}
            >
              <div className="container mx-auto flex items-center justify-center gap-3 flex-wrap text-center">
                <span
                  className="flex-shrink-0 inline-flex items-center text-[0.65rem] font-bold px-3 py-1 rounded-full uppercase tracking-[0.2em]"
                  style={{
                    background: "rgba(201,168,76,0.14)",
                    border: "1px solid rgba(201,168,76,0.55)",
                    color: "var(--vea-or)",
                    fontFamily: "'Montserrat', sans-serif",
                  }}
                >
                  ✦ Nouveau
                </span>
                <p className="text-sm font-medium text-white/95">
                  Offres spéciales Mariages 2026 - Gironde &amp; Bordeaux
                </p>
                <Link href="/mariage">
                  <button
                    className="inline-flex items-center gap-1 text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-widest transition-all hover:gap-2"
                    style={{
                      background: "var(--vea-or)",
                      color: "#0D0D0D",
                      fontFamily: "'Montserrat', sans-serif",
                      boxShadow: "0 4px 14px -4px rgba(201,168,76,0.5)",
                    }}
                  >
                    En savoir plus
                    <ArrowRight className="w-3 h-3" />
                  </button>
                </Link>
              </div>
              <button
                onClick={() => {
                  setShowBanner(false);
                  localStorage.setItem("vea-banner-dismissed", "true");
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-white/10 transition-colors text-white/80"
                aria-label="Fermer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : undefined
        }
      />

      {/* Repère principal (landmark) pour l'accessibilité et les lecteurs d'écran */}
      <main id="contenu">
      {/* ═══════════════════════════════════════════════════════════════════════
          HERO - Cinématique plein écran
      ═══════════════════════════════════════════════════════════════════════ */}
      <section
        className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
        style={{ paddingTop: "0px", paddingBottom: "8rem" }}
      >
        {/* Fond : image aérienne avec overlay sombre + parallaxe */}
        <div className="absolute inset-0" style={{ overflow: "hidden" }}>
          {/* display:contents — le <picture> ne cree aucune boite, l'<img> reste
              l'element mis en page : les classes et le style ci-dessous
              s'appliquent exactement comme avant. */}
          <picture style={{ display: "contents" }}>
            <source
              type="image/webp"
              srcSet="/dji-air3-hero-768.webp 768w, /dji-air3-hero.webp 1500w"
              sizes="100vw"
            />
          <img
            src="/dji-air3-hero.jpg"
            alt="DJI Air 3 Pro - VEA STUDIO Bordeaux"
            width={1500}
            height={844}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="w-full object-cover"
            style={{
              filter: "brightness(0.28) saturate(1.1)",
              height: "115%",
              transform: `translateY(${parallaxOffset}px)`,
              willChange: "transform",
            }}
          />
          </picture>
          {/* Vignette cinématique */}
          <div
            className="absolute inset-0"
            style={{
              background: "radial-gradient(ellipse at center, transparent 30%, rgba(13,13,13,0.7) 100%)",
            }}
          />
          {/* Gradient bas */}
          <div
            className="absolute inset-0"
            style={{
              background: "linear-gradient(to bottom, rgba(13,13,13,0.2) 0%, transparent 35%, rgba(13,13,13,0.65) 75%, #0D0D0D 100%)",
            }}
          />
          {/* Ligne horizontale décorative */}
          <div
            className="absolute left-0 right-0"
            style={{
              bottom: "18%",
              height: "1px",
              background: "linear-gradient(90deg, transparent 0%, rgba(201,168,76,0.15) 30%, rgba(201,168,76,0.3) 50%, rgba(201,168,76,0.15) 70%, transparent 100%)",
            }}
          />
        </div>

        {/* Contenu hero */}
        <div className="relative z-10 container mx-auto text-center px-4" style={{ paddingTop: "140px" }}>

          {/* Logo — apparition SANS opacite initiale.
              Ce logo est le plus grand element de la page : c'est lui que
              Google chronometre. Il partait de `opacity: 0` et n'apparaissait
              qu'apres le montage de React plus trois cents millisecondes, plus
              une transition de 1,2 s. Il etait pourtant precharge et arrivait
              vite — mais un element invisible n'est pas « affiche », donc la
              mesure attendait la fin de l'animation au lieu d'attendre l'image.

              Mesure avant correction, reseau bride : image chargee en 1,2 s,
              mais LCP a 10,8 s.

              L'agrandissement seul conserve l'effet d'arrivee : le logo est
              visible des le premier rendu, simplement un peu plus petit
              pendant un instant. Regle deja appliquee sur handivia. */}
          <div
            className="flex justify-center mb-10"
            style={{
              transform: heroRevealed ? "scale(1)" : "scale(0.88)",
              transition: "transform 1.2s cubic-bezier(0.16, 1, 0.3, 1)",
            }}
          >
            <picture style={{ display: "contents" }}>
            <source type="image/webp" srcSet="/vea-logo-or.webp" />
            <img
              src="/vea-logo-or.png"
              alt="VEA STUDIO"
              width={820}
              height={492}
              loading="eager"
              fetchPriority="high"
              className="h-56 md:h-80 object-contain"
              id="vea-hero-logo"
              style={{ filter: "drop-shadow(0 0 60px rgba(201,168,76,0.2)) drop-shadow(0 0 20px rgba(201,168,76,0.1))" }}
              onError={(e) => {
                (e.target as HTMLImageElement).src = "/logo-noir.png";
                (e.target as HTMLImageElement).style.filter = "invert(1)";
              }}
            />
            </picture>
          </div>

          {/* Badge localisation */}
          <div
            className="flex justify-center mb-8"
            style={{
              opacity: heroRevealed ? 1 : 0,
              transform: heroRevealed ? "translateY(0)" : "translateY(20px)",
              transition: "opacity 0.9s ease 0.2s, transform 0.9s cubic-bezier(0.16,1,0.3,1) 0.2s",
            }}
          >
            <span
              className="inline-flex items-center gap-2 px-5 py-2 text-xs font-bold uppercase tracking-widest"
              style={{
                border: "1px solid rgba(201,168,76,0.5)",
                color: "var(--vea-or)",
                fontFamily: "'Montserrat', sans-serif",
                background: "rgba(201,168,76,0.05)",
                backdropFilter: "blur(8px)",
              }}
            >
              <MapPin className="w-3 h-3" />
              Bordeaux · Gironde · Nouvelle-Aquitaine
            </span>
          </div>

          {/* Titre principal - clip-path reveal */}
          <div className="overflow-hidden mb-1">
            <h1
              className="text-5xl md:text-7xl lg:text-8xl font-bold leading-tight"
              style={{
                fontFamily: "'Playfair Display', serif",
                color: "#FFFFFF",
                clipPath: heroRevealed ? "inset(0 0% 0 0)" : "inset(0 100% 0 0)",
                transition: "clip-path 1.1s cubic-bezier(0.16, 1, 0.3, 1) 0.3s",
                willChange: "clip-path",
                letterSpacing: "-0.01em",
              }}
            >
              Vidéaste Professionnelle
            </h1>
          </div>
          <div className="overflow-hidden mb-6">
            <span
              className="block text-5xl md:text-7xl lg:text-8xl font-bold leading-tight"
              style={{
                fontFamily: "'Playfair Display', serif",
                color: "var(--vea-or)",
                clipPath: heroRevealed ? "inset(0 0% 0 0)" : "inset(0 100% 0 0)",
                transition: "clip-path 1.1s cubic-bezier(0.16, 1, 0.3, 1) 0.5s",
                willChange: "clip-path",
                letterSpacing: "-0.01em",
              }}
            >
              &amp; Pilote de Drone
            </span>
          </div>

          {/* Ligne or animée */}
          <div className="flex justify-center mb-8">
            <div
              style={{
                height: "1px",
                background: "linear-gradient(90deg, transparent, var(--vea-or), transparent)",
                width: heroRevealed ? "280px" : "0px",
                transition: "width 1.4s cubic-bezier(0.16, 1, 0.3, 1) 0.8s",
                willChange: "width",
              }}
            />
          </div>

          {/* Sous-titre */}
          <p
            className="text-lg md:text-xl max-w-2xl mx-auto mb-12"
            style={{
              color: "rgba(255,255,255,0.7)",
              fontFamily: "'Montserrat', sans-serif",
              lineHeight: 1.8,
              opacity: heroRevealed ? 1 : 0,
              transform: heroRevealed ? "translateY(0)" : "translateY(16px)",
              transition: "opacity 0.9s ease 0.9s, transform 0.9s cubic-bezier(0.16,1,0.3,1) 0.9s",
            }}
          >
            Captation vidéo aérienne et au sol pour mariages, immobilier, entreprises et associations.
            Créativité, innovation et expertise pour sublimer votre image.
          </p>

          {/* CTAs */}
          <div
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            style={{
              opacity: heroRevealed ? 1 : 0,
              transform: heroRevealed ? "translateY(0)" : "translateY(20px)",
              transition: "opacity 0.9s ease 1.1s, transform 0.9s cubic-bezier(0.16,1,0.3,1) 1.1s",
            }}
          >
            <Link href="/services">
              <button
                className="btn-cinema btn-shimmer flex items-center gap-2 group"
                style={{ minWidth: "200px" }}
              >
                <FileText className="w-4 h-4" />
                Demande de devis
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </Link>
            <button
              onClick={() => handleWhatsApp("un projet")}
              className="btn-cinema-outline btn-shimmer flex items-center gap-2"
              style={{ minWidth: "200px" }}
            >
              <MessageCircle className="w-4 h-4" />
              Contacter sur WhatsApp
            </button>
          </div>

          {/* Indicateurs de confiance sous les CTAs */}
          <div
            className="flex flex-wrap justify-center gap-6 mt-10"
            style={{
              opacity: heroRevealed ? 1 : 0,
              transition: "opacity 0.9s ease 1.3s",
            }}
          >
            {[
              { icon: Shield, label: "Télépilote DGAC certifié" },
              { icon: Star, label: "150+ projets réalisés" },
              { icon: Zap, label: "Réponse sous 24h" },
            ].map(({ icon: Icon, label }, i) => (
              <div key={i} className="flex items-center gap-2" style={{ color: "rgba(255,255,255,0.5)" }}>
                <Icon className="w-3.5 h-3.5" style={{ color: "var(--vea-or)" }} />
                <span className="text-xs uppercase tracking-wider" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Flèche scroll */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 float-anim flex flex-col items-center gap-1">
          <span className="text-xs uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.3)", fontFamily: "'Montserrat', sans-serif", fontSize: "0.6rem" }}>
            Scroll
          </span>
          <ChevronDown className="w-6 h-6" style={{ color: "rgba(255,255,255,0.35)" }} />
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          MARQUEE - Bande défilante
      ═══════════════════════════════════════════════════════════════════════ */}
      <div
        className="py-5 overflow-hidden relative"
        style={{
          background:
            "linear-gradient(90deg, var(--vea-noir-cinema) 0%, #2A0E12 28%, #3A1118 50%, #2A0E12 72%, var(--vea-noir-cinema) 100%)",
          borderTop: "1px solid rgba(201,168,76,0.18)",
          borderBottom: "1px solid rgba(201,168,76,0.18)",
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
        }}
      >
        <div className="marquee-wrapper">
          <div className="marquee-track">
            {[
              "Drone Professionnel", "·", "Vidéaste Gironde", "·",
              "Mariage Bordeaux", "·", "Immobilier Aérien", "·",
              "Corporate Video", "·", "Télépilote DGAC", "·",
              "Expertise Judiciaire", "·", "Nouvelle-Aquitaine", "·",
              "DJI Air 3 Pro", "·", "4K Ultra HD", "·",
              "Drone Professionnel", "·", "Vidéaste Gironde", "·",
              "Mariage Bordeaux", "·", "Immobilier Aérien", "·",
              "Corporate Video", "·", "Télépilote DGAC", "·",
              "Expertise Judiciaire", "·", "Nouvelle-Aquitaine", "·",
              "DJI Air 3 Pro", "·", "4K Ultra HD", "·",
            ].map((word, i) => (
              <span
                key={i}
                className={word === "·" ? "px-1 text-sm whitespace-nowrap" : "px-5 text-sm font-semibold uppercase tracking-[0.18em] whitespace-nowrap"}
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  color: word === "·" ? "var(--vea-or)" : "rgba(255,255,255,0.9)",
                  fontSize: word === "·" ? "0.55rem" : undefined,
                  opacity: word === "·" ? 0.9 : 1,
                }}
              >
                {word === "·" ? "◆" : word}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          STATS - Chiffres clés avec spotlight
      ═══════════════════════════════════════════════════════════════════════ */}
      {/* Divider animé entre hero et stats */}
      <div className="section-divider" style={{ margin: "0" }} />

      <section
        ref={statsRef as React.RefObject<HTMLElement>}
        className="py-24 spotlight-card"
        style={{
          background: "var(--vea-noir-card)",
          borderBottom: "1px solid var(--vea-gris-bordure)",
        }}
      >
        <div className="container mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 stagger-parent scroll-stagger">
            <AnimatedStat value={150} suffix="+" label="Projets réalisés" icon={Camera} delay={0} />
            <AnimatedStat value={75} suffix="+" label="Mariages filmés" icon={Star} delay={100} />
            <AnimatedStat value={5} suffix="ans" label="D'expérience" icon={Award} delay={200} />
            <AnimatedStat value={75} suffix="km" label="Rayon d'intervention" icon={MapPin} delay={300} />
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          MANIFESTE - Section storytelling
      ═══════════════════════════════════════════════════════════════════════ */}
      <section
        className="py-32 relative overflow-hidden"
        style={{ background: "var(--vea-noir-cinema)" }}
      >
        {/* Fond décoratif : cercle lumineux */}
        <div
          className="absolute pointer-events-none"
          style={{
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "600px",
            height: "600px",
            background: "radial-gradient(circle, rgba(166,30,45,0.06) 0%, transparent 70%)",
            borderRadius: "50%",
          }}
        />
        {/* Lignes décoratives */}
        <div className="absolute left-0 top-0 bottom-0 w-px" style={{ background: "linear-gradient(to bottom, transparent, rgba(201,168,76,0.15), transparent)" }} />
        <div className="absolute right-0 top-0 bottom-0 w-px" style={{ background: "linear-gradient(to bottom, transparent, rgba(201,168,76,0.15), transparent)" }} />

        <div className="container mx-auto max-w-4xl text-center relative z-10">
          <div className="reveal mb-6">
            <span
              className="text-xs uppercase tracking-widest"
              style={{ color: "var(--vea-rouge-bordeaux)", fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.3em" }}
            >
              Notre Philosophie
            </span>
          </div>
          <h2
            className="reveal text-4xl md:text-6xl lg:text-7xl font-bold leading-tight mb-8"
            style={{
              fontFamily: "'Playfair Display', serif",
              color: "white",
              transitionDelay: "100ms",
              lineHeight: 1.15,
            }}
          >
            Chaque image raconte
            <br />
            <em style={{ color: "var(--vea-or)", fontStyle: "italic" }}>une histoire unique</em>
          </h2>
          <div className="flex justify-center mb-10 reveal" style={{ transitionDelay: "150ms" }}>
            <div style={{ width: "60px", height: "1px", background: "linear-gradient(90deg, var(--vea-rouge-bordeaux), var(--vea-or))" }} />
          </div>
          <TypewriterText
            text="Passionnée par la créativité et l'innovation, Valentine Baumberger met son expertise au service de vos projets. Télépilote professionnelle certifiée DGAC, elle capture des images uniques qui racontent votre histoire avec authenticité et émotion - du sol jusqu'au ciel."
            mode="word"
            speed={55}
            delay={300}
            highlightWords={["créativité", "innovation", "DGAC", "authenticité", "émotion"]}
            className="mb-12"
            style={{
              color: "rgba(255,255,255,0.65)",
              fontFamily: "'Montserrat', sans-serif",
              lineHeight: 1.9,
              fontSize: "clamp(1rem, 1.5vw, 1.2rem)",
              maxWidth: "48rem",
              margin: "0 auto 3rem",
              display: "block",
            }}
          />

          {/* Trois piliers */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 scroll-stagger">
            {[
              {
                number: "01",
                title: "Créativité",
                desc: "Chaque projet est une nouvelle toile. Nous concevons des visuels qui marquent les esprits.",
              },
              {
                number: "02",
                title: "Expertise",
                desc: "Télépilote DGAC, équipement professionnel DJI Air 3 Pro, maîtrise technique totale.",
              },
              {
                number: "03",
                title: "Engagement",
                desc: "Votre satisfaction est notre priorité. Nous livrons dans les délais, sans compromis.",
              },
            ].map((pilier, i) => (
              <div
                key={i}
                className="stagger-child text-left p-6 relative"
                style={{
                  border: "1px solid rgba(255,255,255,0.06)",
                  background: "rgba(255,255,255,0.02)",
                  transition: "border-color 0.4s ease, background 0.4s ease",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = "rgba(201,168,76,0.25)";
                  (e.currentTarget as HTMLElement).style.background = "rgba(201,168,76,0.03)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.06)";
                  (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.02)";
                }}
              >
                <div
                  className="text-6xl font-bold mb-4 leading-none" aria-hidden="true"
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    color: "rgba(201,168,76,0.58)",
                    userSelect: "none",
                  }}
                >
                  {pilier.number}
                </div>
                <h3
                  className="text-lg font-bold mb-3"
                  style={{ fontFamily: "'Playfair Display', serif", color: "white" }}
                >
                  {pilier.title}
                </h3>
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: "rgba(255,255,255,0.5)", fontFamily: "'Montserrat', sans-serif" }}
                >
                  {pilier.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          SERVICES - Grille 3D immersive
      ═══════════════════════════════════════════════════════════════════════ */}
      <section
        className="py-24"
        style={{ background: "var(--vea-noir-card)", borderTop: "1px solid var(--vea-gris-bordure)" }}
      >
        <div className="container mx-auto">
          {/* En-tête asymétrique */}
          <div className="max-w-7xl mx-auto mb-16">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div className="scroll-slide-left">
                <span className="badge-cinema mb-4 inline-block">Nos Prestations</span>
                <h2
                  className="text-4xl md:text-6xl font-bold"
                  style={{ fontFamily: "'Playfair Display', serif", lineHeight: 1.1 }}
                >
                  Drone &amp; Vidéo
                  <br />
                  <span style={{ color: "var(--vea-or)" }}>Professionnels</span>
                </h2>
              </div>
              <div className="scroll-slide-right md:max-w-xs">
                <div style={{ width: "40px", height: "1px", background: "var(--vea-rouge-bordeaux)", marginBottom: "12px" }} />
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: "var(--vea-gris-texte)", fontFamily: "'Montserrat', sans-serif" }}
                >
                  Solutions visuelles adaptées à chaque secteur, dans un rayon de 75 km autour de Bordeaux.
                </p>
              </div>
            </div>
          </div>

          {/* Grille services avec tilt 3D */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-7xl mx-auto">
            {services.map((service, index) => (
              <ServiceCard key={index} service={service} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          CALCULATEUR
      ═══════════════════════════════════════════════════════════════════════ */}
      <div style={{ background: "var(--vea-noir-cinema)", borderTop: "1px solid var(--vea-gris-bordure)" }}>
        <Suspense fallback={<div style={{ minHeight: "480px" }} />}>
          <PriceCalculator />
        </Suspense>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          QUIZ
      ═══════════════════════════════════════════════════════════════════════ */}
      <Suspense fallback={<div style={{ minHeight: "420px" }} />}>
        <QuizConversion />
      </Suspense>

      {/* ═══════════════════════════════════════════════════════════════════════
          TÉMOIGNAGES
      ═══════════════════════════════════════════════════════════════════════ */}
      <section
        className="py-24 relative overflow-hidden"
        style={{ background: "var(--vea-noir-card)", borderTop: "1px solid var(--vea-gris-bordure)" }}
      >
        {/* Fond décoratif */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "radial-gradient(ellipse at 80% 50%, rgba(166,30,45,0.04) 0%, transparent 60%)",
          }}
        />
        <div className="container mx-auto max-w-5xl relative z-10">
          <div className="text-center mb-12">
            <span className="badge-cinema mb-4 inline-block reveal">Témoignages</span>
            <h2
              className="text-4xl md:text-5xl font-bold mb-4 reveal"
              style={{ fontFamily: "'Playfair Display', serif", transitionDelay: "100ms" }}
            >
              Ce Que Disent Nos Clients
            </h2>
            <span className="sep-or mx-auto block reveal" style={{ transitionDelay: "150ms" }} />
          </div>
          <div className="reveal" style={{ transitionDelay: "200ms" }}>
            <Suspense fallback={<div style={{ minHeight: "320px" }} />}>
              <TestimonialCarousel />
            </Suspense>
          </div>
          <div className="text-center mt-10 reveal" style={{ transitionDelay: "300ms" }}>
            <Link href="/testimonials">
              <button className="btn-cinema-outline hover-line">
                Voir tous les témoignages
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          À PROPOS - Layout asymétrique premium
      ═══════════════════════════════════════════════════════════════════════ */}
      <section
        className="py-24 relative overflow-hidden"
        style={{ background: "var(--vea-noir-cinema)" }}
      >
        {/* Fond décoratif */}
        <div
          className="absolute right-0 top-0 bottom-0 pointer-events-none"
          style={{
            width: "40%",
            background: "linear-gradient(to left, rgba(201,168,76,0.02), transparent)",
          }}
        />
        <div className="container mx-auto">
          <div className="max-w-6xl mx-auto grid md:grid-cols-5 gap-12 items-center">
            {/* Texte - 3 colonnes */}
            <div className="md:col-span-3 reveal-left">
              <span className="badge-cinema mb-6 inline-block">À propos</span>
              <h2
                className="text-4xl md:text-5xl font-bold mb-4"
                style={{ fontFamily: "'Playfair Display', serif", lineHeight: 1.15 }}
              >
                Valentine
                <br />
                <span style={{ color: "var(--vea-or)" }}>Baumberger</span>
              </h2>
              <span className="sep-or block mb-6" />
              <p
                className="text-base leading-relaxed mb-4"
                style={{ color: "rgba(255,255,255,0.6)", fontFamily: "'Montserrat', sans-serif", lineHeight: 1.9 }}
              >
                Passionnée par la créativité et l'innovation, Valentine met son expertise au service de vos projets.
                Spécialisée dans les prises de vues aériennes par drone, elle capture des images uniques qui racontent
                votre histoire avec authenticité et émotion.
              </p>
              <p
                className="text-base leading-relaxed mb-8"
                style={{ color: "rgba(255,255,255,0.6)", fontFamily: "'Montserrat', sans-serif", lineHeight: 1.9 }}
              >
                Au-delà du drone, VEA STUDIO développe également des solutions créatives en marketing digital,
                création de sites Internet et production de contenu pour les réseaux sociaux.
              </p>

              {/* Certifications */}
              <div className="flex flex-col gap-3">
                {[
                  { icon: Award, text: "Télépilote professionnel certifié DGAC" },
                  { icon: Shield, text: "Assurance responsabilité civile professionnelle" },
                  { icon: Eye, text: "Autorisation de survol en zones réglementées" },
                ].map(({ icon: Icon, text }, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 flex-shrink-0 flex items-center justify-center"
                      style={{ border: "1px solid rgba(201,168,76,0.3)", background: "rgba(201,168,76,0.05)" }}
                    >
                      <Icon className="w-3.5 h-3.5" style={{ color: "var(--vea-or)" }} />
                    </div>
                    <span
                      className="text-sm"
                      style={{ color: "rgba(255,255,255,0.7)", fontFamily: "'Montserrat', sans-serif" }}
                    >
                      {text}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Vidéo - 2 colonnes */}
            <div className="md:col-span-2 reveal-right" style={{ transitionDelay: "100ms" }}>
              <div
                className="overflow-hidden relative group"
                style={{
                  border: "1px solid var(--vea-gris-bordure)",
                  transition: "border-color 0.4s ease",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = "rgba(201,168,76,0.4)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = "var(--vea-gris-bordure)";
                }}
              >
                <VideoWithSound />
                <div
                  className="absolute top-3 left-3 flex items-center gap-2 px-3 py-1.5 text-xs font-bold uppercase tracking-widest"
                  style={{
                    background: "rgba(13,13,13,0.9)",
                    border: "1px solid var(--vea-or)",
                    color: "var(--vea-or)",
                    fontFamily: "'Montserrat', sans-serif",
                  }}
                >
                  <Camera className="w-3 h-3" />
                  Showreel
                </div>
              </div>

              {/* CTA sous la vidéo */}
              <div className="mt-4 flex gap-3">
                <Link href="/services" className="flex-1">
                  <button
                    className="btn-cinema btn-shimmer w-full text-sm justify-center"
                    style={{ padding: "0.75rem 1rem" }}
                  >
                    Demander un devis
                  </button>
                </Link>
                <button
                  onClick={() => handleWhatsApp("un projet")}
                  aria-label="Contacter sur WhatsApp"
                  className="btn-cinema-outline text-sm flex items-center gap-2"
                  style={{ padding: "0.75rem 1rem" }}
                >
                  <MessageCircle className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          RÉALISATIONS - Grille vidéo premium
      ═══════════════════════════════════════════════════════════════════════ */}
      <section
        className="py-24"
        style={{ background: "var(--vea-noir-card)", borderTop: "1px solid var(--vea-gris-bordure)" }}
      >
        <div className="container mx-auto max-w-7xl">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div className="scroll-slide-left">
              <span className="badge-cinema mb-4 inline-block">Portfolio</span>
              <h2
                className="text-4xl md:text-5xl font-bold"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Nos Réalisations
              </h2>
            </div>
            <div className="scroll-slide-right">
              <Link href="/portfolio">
                <button className="btn-cinema-outline flex items-center gap-2">
                  <Play className="w-4 h-4" />
                  Voir tout le portfolio
                </button>
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {[
              {
                src: "https://www.youtube.com/embed/dP1Yaj_MMUs",
                title: "Événement Associatif - Couverture Complète",
                desc: "Captez l'essence de vos événements associatifs avec des prises de vues aériennes et au sol.",
                badge: "Association",
              },
              {
                src: "https://www.youtube.com/embed/O4gCv91yQTk",
                title: "Actions Communautaires - Reportage Engagé",
                desc: "Valorisez vos actions sur le terrain avec des vidéos professionnelles qui racontent votre impact.",
                badge: "Association",
              },
              {
                src: "https://www.youtube.com/embed/bFccJq4JLwg",
                title: "Prises de Vues Aériennes - Perspective Unique",
                desc: "Découvrez vos projets sous un angle spectaculaire grâce au drone.",
                badge: "Drone",
              },
              {
                src: "https://drive.google.com/file/d/1ZqfxXUz3Rda18gMxbnUj9PyrSwycjrZl/preview",
                title: "Projets Associatifs - Vitrine des Initiatives",
                desc: "Présentez vos projets avec des vidéos qui inspirent et mobilisent.",
                badge: "Association",
              },
            ].map((video, i) => (
              <div
                key={i}
                className="card-cinema overflow-hidden scroll-blur-in group"
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                <div className="aspect-video relative overflow-hidden">
                  <Suspense fallback={<div style={{ aspectRatio: "16 / 9" }} />}>
                    <VideoFrame src={video.src} title={video.title} />
                  </Suspense>
                  <div
                    className="absolute top-3 left-3 z-10 text-xs font-bold px-3 py-1 uppercase tracking-widest pointer-events-none"
                    style={{
                      background: "var(--vea-rouge-bordeaux)",
                      color: "white",
                      fontFamily: "'Montserrat', sans-serif",
                    }}
                  >
                    {video.badge}
                  </div>
                </div>
                <div className="p-5">
                  <h3
                    className="text-base font-bold mb-2"
                    style={{ fontFamily: "'Montserrat', sans-serif", color: "white" }}
                  >
                    {video.title}
                  </h3>
                  <p className="text-sm" style={{ color: "var(--vea-gris-texte)", fontFamily: "'Montserrat', sans-serif" }}>
                    {video.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          CTA FINAL - Bande plein écran
      ═══════════════════════════════════════════════════════════════════════ */}
      {/* Conseils & inspirations — maillage interne vers le blog */}
      <section className="py-24" style={{ background: "#0D0D0D" }}>
        <div className="container mx-auto max-w-6xl px-4">
          <div className="text-center mb-14">
            <span
              className="text-xs uppercase tracking-widest"
              style={{ color: "var(--vea-or)", fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.3em" }}
            >
              Le blog VEA STUDIO
            </span>
            <h2
              className="text-3xl md:text-5xl font-bold mt-3"
              style={{ fontFamily: "'Playfair Display', serif", color: "white", lineHeight: 1.15 }}
            >
              Conseils &amp; inspirations
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredArticles
              .map((article) => (
                <Link key={article.id} href={`/blog/${article.id}`} className="group block">
                  <article
                    className="h-full flex flex-col transition-all duration-300"
                    style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
                  >
                    <div className="overflow-hidden" style={{ aspectRatio: "16/10" }}>
                      <img
                        src={article.image}
                        alt={article.title}
                        width={640}
                        height={400}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <span
                        className="text-[0.65rem] uppercase tracking-widest mb-2"
                        style={{ color: "var(--vea-or)", fontFamily: "'Montserrat', sans-serif" }}
                      >
                        {article.category}
                      </span>
                      <h3
                        className="text-base font-bold mb-2 transition-colors group-hover:text-white"
                        style={{ fontFamily: "'Playfair Display', serif", color: "rgba(255,255,255,0.9)", lineHeight: 1.3 }}
                      >
                        {article.title}
                      </h3>
                      <span
                        className="text-xs mt-auto pt-2"
                        style={{ color: "var(--vea-or)", fontFamily: "'Montserrat', sans-serif" }}
                      >
                        Lire l'article →
                      </span>
                    </div>
                  </article>
                </Link>
              ))}
          </div>

          <div className="text-center mt-12">
            <Link
              href="/blog"
              className="inline-block text-xs font-bold uppercase tracking-widest px-6 py-3 transition-all hover:opacity-90"
              style={{ border: "1px solid var(--vea-or)", color: "var(--vea-or)", fontFamily: "'Montserrat', sans-serif" }}
            >
              Voir tous les articles
            </Link>
          </div>
        </div>
      </section>

      <section
        className="py-32 relative overflow-hidden"
        style={{ background: "var(--vea-rouge-bordeaux)" }}
      >
        {/* Fond décoratif */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "radial-gradient(ellipse at center, rgba(201,168,76,0.1) 0%, transparent 70%)",
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E\")",
            opacity: 0.04,
          }}
        />

        <div className="container mx-auto max-w-3xl text-center relative z-10">
          <div className="scroll-badge-pop mb-4">
            <span
              className="text-xs uppercase tracking-widest"
              style={{ color: "rgba(255,255,255,0.6)", fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.3em" }}
            >
              Commençons ensemble
            </span>
          </div>
          <h2
            className="text-4xl md:text-6xl font-bold mb-4 scroll-scale-up"
            style={{ fontFamily: "'Playfair Display', serif", color: "white", lineHeight: 1.15 }}
          >
            Une Question ?
            <br />
            Un Projet ?
          </h2>
          <div className="flex justify-center mb-8 reveal" style={{ transitionDelay: "150ms" }}>
            <div style={{ width: "60px", height: "1px", background: "rgba(201,168,76,0.6)" }} />
          </div>
          <p
            className="text-lg mb-12 reveal"
            style={{
              color: "rgba(255,255,255,0.8)",
              fontFamily: "'Montserrat', sans-serif",
              transitionDelay: "200ms",
              lineHeight: 1.8,
            }}
          >
            Contactez Valentine pour discuter de votre projet et obtenir un devis personnalisé.
            Réponse garantie sous 24h.
          </p>

          <div
            className="flex flex-col sm:flex-row gap-4 justify-center items-center reveal"
            style={{ transitionDelay: "300ms" }}
          >
            <button
              onClick={() => handleWhatsApp("un projet personnalisé")}
              className="btn-shimmer flex items-center gap-2 group"
              style={{
                background: "white",
                color: "var(--vea-rouge-bordeaux)",
                padding: "0.875rem 2rem",
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 700,
                fontSize: "0.875rem",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                border: "none",
                transition: "transform 0.2s ease, box-shadow 0.3s ease",
                minWidth: "200px",
                justifyContent: "center",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.transform = "translateY(-2px)";
                (e.currentTarget as HTMLElement).style.boxShadow = "0 8px 30px rgba(0,0,0,0.2)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.transform = "translateY(0)";
                (e.currentTarget as HTMLElement).style.boxShadow = "none";
              }}
            >
              <MessageCircle className="w-4 h-4" />
              Contacter sur WhatsApp
            </button>
            <button
              onClick={() => handleEmail("un projet personnalisé")}
              className="btn-shimmer flex items-center gap-2"
              style={{
                background: "transparent",
                color: "white",
                padding: "0.875rem 2rem",
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 600,
                fontSize: "0.875rem",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                border: "1px solid rgba(255,255,255,0.5)",
                transition: "border-color 0.3s ease, transform 0.2s ease",
                minWidth: "200px",
                justifyContent: "center",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.9)";
                (e.currentTarget as HTMLElement).style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.5)";
                (e.currentTarget as HTMLElement).style.transform = "translateY(0)";
              }}
            >
              <Mail className="w-4 h-4" />
              Envoyer un Email
            </button>
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#0D0D0D] border-t border-white/10">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
            VEA Studio, la signature créative de vos projets audiovisuels
          </h2>
          <div className="space-y-6 text-gray-300 leading-relaxed">
            <p>
              VEA Studio est un studio de création audiovisuelle et d'identité de marque. Notre métier consiste à
              transformer une intention, un message ou une ambition en une expérience visuelle mémorable. Qu'il
              s'agisse d'un film de marque, d'une vidéo de présentation produit, d'un habillage motion ou d'une
              refonte complète d'identité visuelle, nous concevons chaque projet comme une histoire à raconter,
              portée par une esthétique soignée et une exigence de qualité à chaque étape.
            </p>
            <h3 className="text-xl font-semibold text-white pt-2">Production audiovisuelle et films de marque</h3>
            <p>
              De l'écriture du concept au montage final, nous accompagnons la réalisation de vos contenus vidéo :
              films institutionnels, capsules pour les réseaux sociaux, interviews, aftermovies d'événements ou
              démonstrations produit. Chaque production est pensée pour un objectif précis — notoriété,
              conversion, recrutement, formation — et pour le canal de diffusion visé. Nous maîtrisons l'ensemble
              de la chaîne, du tournage à l'étalonnage, en passant par le sound design, afin de livrer des images
              qui captent l'attention dès les premières secondes.
            </p>
            <h3 className="text-xl font-semibold text-white pt-2">Identité visuelle et direction artistique</h3>
            <p>
              Une marque forte repose sur une identité cohérente et reconnaissable. Nous concevons logos, chartes
              graphiques, univers de couleurs, typographies et systèmes visuels qui traduisent la personnalité de
              votre organisation et se déclinent avec cohérence sur tous vos supports, du digital à l'impression.
              Notre approche associe la rigueur d'une direction artistique et la sensibilité d'un travail
              sur-mesure, pour que votre image se distingue durablement de celle de vos concurrents.
            </p>
            <h3 className="text-xl font-semibold text-white pt-2">Motion design et animation</h3>
            <p>
              Le mouvement donne vie à vos idées. Nos animations et habillages motion design mettent en scène vos
              données, vos concepts et vos messages avec dynamisme et clarté. Génériques, intros animées,
              infographies vidéo, transitions et micro-animations : nous utilisons l'animation comme un langage à
              part entière pour rendre l'information plus lisible, plus engageante et plus mémorable.
            </p>
            <h3 className="text-xl font-semibold text-white pt-2">Un accompagnement de bout en bout</h3>
            <p>
              Travailler avec VEA Studio, c'est bénéficier d'un interlocuteur unique, à l'écoute, qui vous
              accompagne de la définition du besoin jusqu'à la livraison des fichiers finaux. Nous prenons le temps
              de comprendre votre secteur, vos publics et vos contraintes pour proposer des créations à la fois
              esthétiques et efficaces. Studio du groupe Handivia, nous partageons une culture d'exigence, de
              proximité et d'engagement au service de la réussite de chaque projet qui nous est confié.
            </p>
            <h3 className="text-xl font-semibold text-white pt-2">Des créations pour tous les secteurs</h3>
            <p>
              Nous collaborons avec des entreprises, des associations, des institutions et des marques de tous
              horizons : industrie, services, santé, culture, événementiel, formation ou secteur inclusif. Cette
              diversité nourrit notre créativité et nous permet d'adapter le ton, le rythme et l'esthétique de
              chaque production à la réalité de votre marché et aux attentes de votre audience. Du format court
              pensé pour les réseaux sociaux au film premium diffusé lors d'un événement, chaque livrable est
              calibré pour servir vos objectifs de communication.
            </p>
            <h3 className="text-xl font-semibold text-white pt-2">Pourquoi choisir VEA Studio</h3>
            <p>
              Choisir VEA Studio, c'est faire le pari d'une création qui a du sens et du caractère. Nous refusons
              les formats génériques et les recettes toutes faites : chaque projet mérite une écriture visuelle qui
              lui est propre. Notre engagement se mesure à la qualité de l'écoute, à la transparence de nos
              échanges et à la précision de l'exécution. Parlons de votre projet : décrivez-nous votre besoin et
              nous vous proposerons une approche créative sur-mesure, un accompagnement clair et un rendu à la
              hauteur de vos ambitions. Nous croyons qu'une bonne création naît d'une vraie collaboration : plus
              vous partagez votre vision, vos valeurs et vos objectifs, plus le résultat vous ressemblera. C'est
              cette relation de confiance, construite dans la durée, qui fait la différence entre une vidéo
              oubliée et une réalisation dont vous serez fier. Contactez-nous dès aujourd'hui pour donner vie à
              votre prochain projet audiovisuel avec un partenaire créatif engagé à vos côtés du premier
              échange jusqu'à la diffusion finale.
            </p>
          </div>
        </div>
      </section>
      </main>

      <SiteFooter />
    </div>
  );
}
